import { useState, useCallback } from 'react';
import cn from 'classnames';
import IconArrowDownBold from '@creditflowresearch/uilib/Icon/IconArrowDownBold';

import Button from '../Button';
import ButtonGroup from '../ButtonGroup';
import Menu from '../Menu/Menu';
import type { ButtonSize } from '../Button/types';
import type { IButtonDropDownProps } from './types';
import { chevronIconSize, DEFAULT_TRIGGER_ARIA_LABEL } from './constants';
import styles from './buttonDropDown.module.scss';

const actionPaddingClass: Record<ButtonSize, string> = {
  S: styles.actionS,
  M: styles.actionM,
  L: styles.actionL,
};

const ButtonDropDown = ({
  dataTest,
  actionDataTest,
  triggerDataTest,
  label,
  children,
  onClick,
  variant = 'solid',
  size = 'M',
  color = 'primary',
  icon,
  disabled = false,
  loading = false,
  placement = 'bottom-end',
  open: controlledOpen,
  onOpenChange,
  className,
  ariaLabel,
  triggerAriaLabel = DEFAULT_TRIGGER_ARIA_LABEL,
}: IButtonDropDownProps): React.ReactElement => {
  // Uncontrolled state, overridden by controlled props
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback((nextOpen: boolean): void => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  }, [isControlled, onOpenChange]);

  const isDisabled = disabled || loading;

  // Resolve aria-label for the group
  const groupAriaLabel = ariaLabel ?? (typeof label === 'string' ? label : undefined);

  return (
    <ButtonGroup
      variant={ variant }
      size={ size }
      color={ color }
      ariaLabel={ groupAriaLabel }
      className={ cn(styles.group, className) }
    >
      { /* Action button (left half) */ }
      <Button
        dataTest={ actionDataTest ?? dataTest }
        onClick={ onClick }
        disabled={ isDisabled }
        loading={ loading }
        icon={ icon }
        className={ actionPaddingClass[size] }
      >
        { label }
      </Button>

      { /* Trigger button (right half) with menu */ }
      <Menu
        dataTest={ `${ dataTest }Menu` }
        open={ isOpen }
        setOpen={ setOpen }
        disabled={ isDisabled }
        placement={ placement }
        offsetOptions={ { mainAxis: 5 } }
        contentClassName={ styles.menu }
        initialFocus="firstItem"
        returnFocusOnClose
        closeOnItemClick
        trigger={ ({ referenceProps }) => (
          <Button
            dataTest={ triggerDataTest ?? `${ dataTest }Trigger` }
            icon={ <IconArrowDownBold className={ styles.chevronIcon } /> }
            iconSize={ chevronIconSize[size] }
            disabled={ isDisabled }
            ariaLabel={ triggerAriaLabel }
            className={ styles.trigger }
            { ...referenceProps }
          />
        ) }
      >
        { children }
      </Menu>
    </ButtonGroup>
  );
};

export default ButtonDropDown;
